// src/features/auth/AnonAuthProvider.tsx
"use client";
import {
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useState } from "react";

import { clientAuth } from "@/lib/firebase/client";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAnonymous: boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  error: null,
  isAnonymous: false,
});

export function useAnonAuth() {
  return useContext(AuthContext);
}

interface AnonAuthProviderProps {
  children: React.ReactNode;
}

export function AnonAuthProvider({ children }: AnonAuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isAnonymous: false,
  });

  useEffect(() => {
    const auth = clientAuth();

    // Set persistence to local storage
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error("Failed to set auth persistence:", error);
      setAuthState((prev) => ({
        ...prev,
        error: "Failed to set auth persistence",
      }));
    });

    let sessionExchanged = false;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          // No user - sign in anonymously
          setAuthState((prev) => ({
            ...prev,
            loading: true,
            error: null,
          }));
          await signInAnonymously(auth);
          return; // This will trigger onAuthStateChanged again with the new user
        }

        // User exists - update state and exchange session if needed
        const isAnonymous = user.isAnonymous;
        setAuthState({
          user,
          loading: false,
          error: null,
          isAnonymous,
        });

        // Exchange Firebase ID token for session cookie (only once per session)
        if (!sessionExchanged) {
          sessionExchanged = true;
          try {
            const idToken = await user.getIdToken();
            const response = await fetch("/api/session", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify({ idToken }),
            });

            if (!response.ok) {
              // Retry once after a delay
              await new Promise((resolve) => setTimeout(resolve, 1000));
              await fetch("/api/session", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "same-origin",
                body: JSON.stringify({ idToken }),
              });
            }
          } catch (error) {
            console.warn("Failed to exchange session cookie:", error);
            // Don't set error state - the app can still work with Firebase auth
          }
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        setAuthState({
          user: null,
          loading: false,
          error: "Authentication failed",
          isAnonymous: false,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
}
